<script setup lang="ts">
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    const { $theme } = useNuxtApp();

    const props = defineProps<FormInputProps>();
    const inputData: FormInputData = useFormInput(props);
    const input = ref<HTMLInputElement | null>(null);
    // `update:locale` is relayed from FormElement so a parent can drive several
    // translated fields from one locale (`v-model:locale`, TDD-3784). Without the
    // relay the event dies on FormElement and never reaches the parent.
    const emit = defineEmits(['focus', 'blur', 'input', 'update:locale']);

    const hasIconLeft = computed(() => !!useSlots()['icon-left']);
    const hasIconRight = computed(() => !!useSlots()['icon-right']);

    const hasError = computed(() => props.form.errors?.has(props.inputName));

    // See `selectOnFocus` in `useFormInput`: opt-in, so desktop keeps click-to-caret.
    const onFocus = () => {
        emit('focus');
        if (props.selectOnFocus) input.value?.select();
    };

    onMounted(async () => {
        if (props.autofocus && input.value) {
            await wait(250);
            input.value?.focus();
        }
    });
</script>

<template>
    <FormElement
        :input-data="inputData"
        @update:locale="emit('update:locale', $event)"
    >
        <template #label-right>
            <slot name="label-right" />
        </template>

        <slot name="before-input" />
        <div
            v-if="inputData.size && (hasIconLeft || hasIconRight || inputData.validated || hasError)"
            class="relative"
        >
            <div
                v-if="hasIconLeft"
                class="absolute left-0 top-0 bottom-0 flex items-center pl-3 pointer-events-none text-[#a1a1aa]"
            >
                <slot name="icon-left" />
            </div>
            <input
                :id="inputData.inputId.value"
                ref="input"
                v-model="inputData.model.value"
                :class="[inputData.className.value, hasIconLeft ? '!pl-9' : '', hasIconRight || inputData.validated || hasError ? '!pr-9' : '']"
                :name="inputData.props.inputName"
                :placeholder="inputData.placeholder.value"
                :type="inputData.props.inputType"
                :inputmode="inputData.props.inputType === 'number' ? 'decimal' : undefined"
                :disabled="inputData.props.disabled"
                :step="inputData.props.step"
                @focus="onFocus"
                @blur="emit('blur')"
                @input="emit('input', ($event.target as HTMLInputElement).value)"
            />
            <div
                v-if="hasIconRight || inputData.validated || hasError"
                class="absolute right-0 top-0 bottom-0 flex items-center pr-3 pointer-events-none"
            >
                <slot name="icon-right">
                    <i
                        v-if="inputData.validated && !hasError"
                        class="fa-solid fa-circle-check text-[#27a476]"
                    />
                    <i
                        v-else-if="hasError"
                        class="fa-solid fa-circle-exclamation text-[#ff4e4e]"
                    />
                </slot>
            </div>
        </div>
        <input
            v-else
            :id="inputData.inputId.value"
            ref="input"
            v-model="inputData.model.value"
            :class="inputData.className.value"
            :name="inputData.props.inputName"
            :placeholder="inputData.placeholder.value"
            :type="inputData.props.inputType"
            :inputmode="inputData.props.inputType === 'number' ? 'decimal' : undefined"
            :disabled="inputData.props.disabled"
            :step="inputData.props.step"
            @focus="onFocus"
            @blur="emit('blur')"
            @input="emit('input', ($event.target as HTMLInputElement).value)"
        />
        <slot name="after-input" />
    </FormElement>
</template>
